import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Menu, X, Sun, Moon } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../contexts/ThemeContext'
import { useLanguage } from '../contexts/LanguageContext'
import { LanguageSelector } from './LanguageSelector'
import PWAStatusIndicator from './PWAStatusIndicator'

export function Navigation() {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const { theme, toggleTheme } = useTheme()
  const { t } = useTranslation()
  const { isRTL } = useLanguage()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background/80 backdrop-blur-md border-b border-border' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center"
            >
              <img
                src={theme === 'light' ? '/images/logo-dark.png' : '/images/logo-light.png'}
                alt="Karwan e Karbala o Sham"
                className="h-10 w-auto"
              />
            </motion.div>
            <span className="text-xl font-bold text-foreground">
              {t('home.title')}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className={`hidden md:flex items-center space-x-6 ${isRTL ? 'space-x-reverse' : ''}`}>
            <Link 
              to="/" 
              className="text-foreground/70 hover:text-foreground px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              {t('navigation.home')}
            </Link>
            <Link 
              to="/hotels" 
              className="text-foreground/70 hover:text-foreground px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              {t('navigation.hotels', 'Hotels')}
            </Link>
            <Link 
              to="/testimonials" 
              className="text-foreground/70 hover:text-foreground px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              {t('navigation.testimonials', 'Testimonials')}
            </Link>
            <Link 
              to="/contact" 
              className="text-foreground/70 hover:text-foreground px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              {t('navigation.contact')}
            </Link>
            <Link 
              to="/compliance" 
              className="text-foreground/70 hover:text-foreground px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              {t('navigation.compliance')}
            </Link>
            
            {/* PWA Status Indicator */}
            <PWAStatusIndicator />

            {/* Language Selector */}
            <LanguageSelector />         
            
            {/* Theme Toggle */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleTheme}
              className="p-2 rounded-md bg-secondary hover:bg-secondary/80 transition-colors duration-200"
            >
              {theme === 'light' ? (
                <Moon className="h-5 w-5 text-foreground" />
              ) : (
                <Sun className="h-5 w-5 text-foreground" />
              )}
            </motion.button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsOpen(!isOpen)}
              className="p-1.5 rounded-md bg-secondary"
            >
              {isOpen ? <X className="h-5 w-5 text-foreground" /> : <Menu className="h-5 w-5 text-foreground" />}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden bg-background/95 backdrop-blur-md border-b border-border"
        >
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {/* Home link with icons */}
            <div className="flex items-center justify-between px-3 py-2">
              <Link
                to="/"
                className="text-foreground/70 hover:text-foreground text-base font-medium"
                onClick={() => setIsOpen(false)}
              >
                {t('navigation.home')}
              </Link>
              <div className={`flex items-center space-x-2 ${isRTL ? 'space-x-reverse' : ''}`}>
                <PWAStatusIndicator />
                <LanguageSelector />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={toggleTheme}
                  className="p-2 rounded-md bg-secondary hover:bg-secondary/80 transition-colors"
                >
                  {theme === 'light' ? (
                    <Moon className="h-5 w-5 text-foreground" />
                  ) : (
                    <Sun className="h-5 w-5 text-foreground" />
                  )}
                </motion.button>
              </div>
            </div>
            
            {/* Hotels link */}
            <Link
              to="/hotels"
              className="block px-4 py-2 text-foreground/70 hover:text-foreground hover:bg-secondary rounded-md transition-colors duration-200"
              onClick={() => setIsOpen(false)}
            >
              {t('navigation.hotels', 'Hotels')}
            </Link>
            {/* Testimonials link */}
            <Link
              to="/testimonials"
              className="text-foreground/70 hover:text-foreground block px-3 py-2 rounded-md text-base font-medium hover:bg-secondary transition-colors duration-200"
              onClick={() => setIsOpen(false)}
            >
              {t('navigation.testimonials', 'Testimonials')}
            </Link>
            {/* Contact link */}
            <Link
              to="/contact"
              className="text-foreground/70 hover:text-foreground block px-3 py-2 rounded-md text-base font-medium"
              onClick={() => setIsOpen(false)}
            >
              {t('navigation.contact')}
            </Link>
            {/* Compliance link */}
            <Link
              to="/compliance"
              className="text-foreground/70 hover:text-foreground block px-3 py-2 rounded-md text-base font-medium"
              onClick={() => setIsOpen(false)}
            >
              {t('navigation.compliance')}
            </Link>
          </div>
        </motion.div>
      )}
    </motion.nav>
  )
}
